import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  Checkbox,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RoomIcon from '@mui/icons-material/Room';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { getRoomAllocationAvailableRooms, getRatesByRatePlan, getHotelRatePlans } from '../../services/api';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerPanelSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerAuditButtonSx,
  getBookingDrawerInputBg,
  getBookingDrawerDividerColor,
  createBookingDrawerOverlayProps,
  BOOKING_DRAWER_ACCENT,
} from '../../utils/bookingDrawerLayout';
import { getDrawerRootSx } from '../../utils/drawerOverlayProps';
import { getBookingCurrencySymbol } from '../../utils/bookingPaymentSummary';
import {
  getCheckInRoomHoldChipColor,
  getCheckInRoomHoldLabel,
  getCheckInRoomHoldOverlaySx,
  getCheckInRoomHoldReason,
  getCheckInRoomHoldTooltip,
} from '../../utils/checkInRoomHold';
import moment from 'moment';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const UpgradeRoomDrawer = ({
  open,
  onClose,
  onUpgradeSuccess,
  bookingId,
  group,
  booking,
  roomTypes,
  ratePlans,
  getAvailableRoomTypes,
}) => {
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const bookingCurrencySymbol = getBookingCurrencySymbol(booking?.currency || 'INR');
  const isOtaBooking = String(booking?.booking_source || '').toLowerCase() === 'ota';
  const bookingChannelId = booking?.channel?.id ?? booking?.channel_id ?? null;
  const { selectMenuProps } = useMemo(
    () => createBookingDrawerOverlayProps(isDark),
    [isDark],
  );

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => setCurrentTheme(newTheme));
    return cleanup;
  }, []);

  const getItemDateStr = useCallback((item) => {
    if (!item?.date) return '';
    return moment.isMoment(item.date)
      ? item.date.format('YYYY-MM-DD')
      : String(item.date).slice(0, 10);
  }, []);

  const isCheckedOutItem = useCallback((item) => {
    const raw = item?.checkout;
    if (typeof raw === 'string') {
      return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
    }
    return raw === true || raw === 1;
  }, []);

  const activeGroupItem = useMemo(() => {
    const items = [...(group?.items || [])];
    if (items.length === 0) return null;
    const todayStr = moment().format('YYYY-MM-DD');
    const sorted = items.sort((a, b) => {
      const aDate = getItemDateStr(a);
      const bDate = getItemDateStr(b);
      if (aDate !== bDate) return aDate.localeCompare(bDate);
      return (a?.id || 0) - (b?.id || 0);
    });
    return (
      sorted.find((it) => {
        const itemDate = getItemDateStr(it);
        return !isCheckedOutItem(it) && itemDate === todayStr;
      }) ||
      sorted.find((it) => {
        const itemDate = getItemDateStr(it);
        return !isCheckedOutItem(it) && itemDate >= todayStr;
      }) ||
      sorted.find((it) => {
        return !isCheckedOutItem(it);
      }) ||
      sorted.find((it) => {
        const itemDate = getItemDateStr(it);
        return itemDate === todayStr;
      }) ||
      sorted.find((it) => {
        const itemDate = getItemDateStr(it);
        return itemDate >= todayStr;
      }) ||
      sorted[0]
    );
  }, [group?.items, getItemDateStr, isCheckedOutItem]);
  const checkIn = booking?.check_in_date ? moment(booking.check_in_date).format('YYYY-MM-DD') : '';
  const checkOut = booking?.check_out_date ? moment(booking.check_out_date).format('YYYY-MM-DD') : '';

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
  const [allocationData, setAllocationData] = useState(null);
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState(null);
  const [selectedRatePlanId, setSelectedRatePlanId] = useState(null);
  const [otaRatePlans, setOtaRatePlans] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [amountPerNight, setAmountPerNight] = useState('');
  const [amountError, setAmountError] = useState('');
  const [upgradeAdults, setUpgradeAdults] = useState(1);
  const [upgradeChildren, setUpgradeChildren] = useState(0);
  const selectableRatePlans = isOtaBooking
    ? (Array.isArray(otaRatePlans) ? otaRatePlans : [])
    : (Array.isArray(ratePlans) ? ratePlans : []);
  const amountPerNightRef = useRef('');
  const upgradeAdultsRef = useRef(1);
  const upgradeChildrenRef = useRef(0);

  const parseRate = useCallback((value, fallback = 0) => {
    const parsed = parseFloat(value);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
    const fallbackParsed = parseFloat(fallback);
    return Number.isFinite(fallbackParsed) && fallbackParsed >= 0 ? fallbackParsed : 0;
  }, []);

  const getRoomTypePricingMeta = useCallback((roomTypeId) => {
    const roomTypeNum = Number(roomTypeId || 0);
    if (!roomTypeNum) return null;
    const sourceFromMaster = (roomTypes || []).find((rt) => Number(rt?.id || 0) === roomTypeNum) || null;
    const sourceFromAvailRaw =
      (availableRoomTypes || []).find(
        (rt) => Number(rt?.id ?? rt?.room_type?.id ?? 0) === roomTypeNum
      ) || null;
    const sourceFromAvail = sourceFromAvailRaw?.room_type || sourceFromAvailRaw || null;
    const source = sourceFromMaster || sourceFromAvail || {};

    const baseRate = parseRate(
      source?.base_rate ??
      sourceFromAvailRaw?.base_rate ??
      source?.final_rate ??
      sourceFromAvailRaw?.final_rate ??
      source?.price,
      0
    );
    const singleRate = parseRate(source?.single_occupancy_rate, baseRate);
    const extraAdultRate = parseRate(source?.extra_adult_rate, 0);
    const extraChildRate = parseRate(source?.extra_child_rate, 0);
    const baseAdults = Math.max(
      1,
      Number(source?.base_adults ?? sourceFromAvailRaw?.base_adults ?? 1) || 1
    );
    const baseChildren = Math.max(
      0,
      Number(source?.base_children ?? sourceFromAvailRaw?.base_children ?? 0) || 0
    );
    const maxGuests = Math.max(
      1,
      Number(source?.max_guests ?? sourceFromAvailRaw?.max_guests ?? 4) || 4
    );
    return {
      baseRate,
      singleRate,
      extraAdultRate,
      extraChildRate,
      baseAdults,
      baseChildren,
      maxGuests,
    };
  }, [roomTypes, availableRoomTypes, parseRate]);

  const computeAmountPerNight = useCallback((pricingMeta, adultsInput, childrenInput, fallback = 0) => {
    if (!pricingMeta) return parseRate(fallback, 0);
    const adults = Math.max(0, Number(adultsInput || 0));
    const children = Math.max(0, Number(childrenInput || 0));
    const baseNightRate = adults <= 1 ? pricingMeta.singleRate : pricingMeta.baseRate;
    const extraAdults = Math.max(0, adults - pricingMeta.baseAdults);
    const extraChildren = Math.max(0, children - pricingMeta.baseChildren);
    const total =
      baseNightRate +
      (extraAdults * pricingMeta.extraAdultRate) +
      (extraChildren * pricingMeta.extraChildRate);
    const next = parseRate(total, fallback);
    return Math.max(0, Math.round(next * 100) / 100);
  }, [parseRate]);

  const fetchData = useCallback(async (includeRoomTypeId = null) => {
    if (!open || !bookingId || !getAvailableRoomTypes || !checkIn || !checkOut) return;
    setLoading(true);
    setError(null);
    try {
      const allocParams = includeRoomTypeId != null ? { include_room_type_ids: [Number(includeRoomTypeId)] } : {};
      const [availRes, allocRes] = await Promise.all([
        getAvailableRoomTypes(checkIn, checkOut, { exclude_booking_id: bookingId }),
        getRoomAllocationAvailableRooms(bookingId, allocParams),
      ]);
      const roomTypesList = availRes?.room_types || [];
      setAvailableRoomTypes(roomTypesList);
      setAllocationData(allocRes);

      if (activeGroupItem) {
        const currentRtId = activeGroupItem.room_type_id ?? activeGroupItem.room_type?.id;
        const currentRpId = activeGroupItem.rate_plan_id ?? activeGroupItem.rate_plan?.id;
        const currentRoomId = activeGroupItem.allocated_room_id ?? activeGroupItem.allocated_room?.id;
        const rtId = currentRtId != null ? currentRtId : (roomTypesList[0]?.id ?? null);
        const currentAdults = Math.max(0, Number(activeGroupItem?.adults ?? 1) || 1);
        const currentChildren = Math.max(0, Number(activeGroupItem?.children ?? 0) || 0);
        setSelectedRoomTypeId(rtId);
        setSelectedRatePlanId(currentRpId ?? null);
        setSelectedRoomId(currentRoomId ?? null);
        setUpgradeAdults(currentAdults);
        setUpgradeChildren(currentChildren);
      }
      const existingSequenceAmount = parseFloat(activeGroupItem?.price);
      if (Number.isFinite(existingSequenceAmount) && existingSequenceAmount >= 0) {
        setAmountPerNight(String(Number(existingSequenceAmount.toFixed(2))));
      } else {
        setAmountPerNight('');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [open, bookingId, checkIn, checkOut, getAvailableRoomTypes, activeGroupItem]);

  useEffect(() => {
    if (open && bookingId && group) {
      const currentRtId = activeGroupItem && (activeGroupItem.room_type_id ?? activeGroupItem.room_type?.id);
      fetchData(currentRtId ?? undefined);
    } else {
      setAvailableRoomTypes([]);
      setAllocationData(null);
      setOtaRatePlans([]);
      setSelectedRoomTypeId(null);
      setSelectedRatePlanId(null);
      setSelectedRoomId(null);
      setAmountPerNight('');
      setAmountError('');
      setUpgradeAdults(1);
      setUpgradeChildren(0);
      setError(null);
    }
  }, [open, bookingId, group?.key, fetchData, activeGroupItem]);

  useEffect(() => {
    if (!open || !isOtaBooking || !selectedRoomTypeId) {
      if (!isOtaBooking) setOtaRatePlans([]);
      return;
    }
    let cancelled = false;
    const currentRpId = activeGroupItem?.rate_plan_id ?? activeGroupItem?.rate_plan?.id ?? null;
    getHotelRatePlans({
      params: {
        room_type_id: selectedRoomTypeId,
        platform: 'cm',
        ...(bookingChannelId ? { channel_id: bookingChannelId } : {}),
        ...(currentRpId ? { include_rate_plan_id: currentRpId } : {}),
      },
    })
      .then((data) => {
        if (cancelled) return;
        const plans = data?.rate_plans || data || [];
        setOtaRatePlans(Array.isArray(plans) ? plans : []);
      })
      .catch(() => {
        if (!cancelled) setOtaRatePlans([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isOtaBooking, selectedRoomTypeId, bookingChannelId, activeGroupItem]);

  // When user selects a different room type (e.g. for upgrade), ensure we have rooms for that type
  useEffect(() => {
    if (!open || !bookingId || selectedRoomTypeId == null) return;
    const hasSelectedType = (allocationData?.room_types || []).some(
      (rt) => (rt.room_type?.id ?? rt.id) === selectedRoomTypeId
    );
    if (hasSelectedType) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getRoomAllocationAvailableRooms(bookingId, {
          include_room_type_ids: [Number(selectedRoomTypeId)],
        });
        if (!cancelled) setAllocationData(res);
      } catch (_) {
        if (!cancelled) setError('Failed to load rooms for selected type.');
      }
    })();
    return () => { cancelled = true; };
  }, [open, bookingId, selectedRoomTypeId, allocationData?.room_types]);

  useEffect(() => {
    amountPerNightRef.current = amountPerNight;
  }, [amountPerNight]);

  useEffect(() => {
    upgradeAdultsRef.current = upgradeAdults;
    upgradeChildrenRef.current = upgradeChildren;
  }, [upgradeAdults, upgradeChildren]);

  useEffect(() => {
    if (!open || !selectedRoomTypeId) return;
    let cancelled = false;

    const syncAmountFromDailyRate = async () => {
      const pricingMeta = getRoomTypePricingMeta(selectedRoomTypeId);
      const currentAdults = upgradeAdultsRef.current;
      const currentChildren = upgradeChildrenRef.current;
      const existingAmount = parseRate(amountPerNightRef.current, 0);
      const activeGroupTypeId = Number(
        activeGroupItem?.room_type_id ?? activeGroupItem?.room_type?.id ?? 0
      );
      const activeGroupRatePlanIdRaw =
        activeGroupItem?.rate_plan_id ?? activeGroupItem?.rate_plan?.id ?? null;
      const selectedRatePlanNum = selectedRatePlanId != null ? Number(selectedRatePlanId) : null;
      const activeGroupRatePlanNum =
        activeGroupRatePlanIdRaw != null ? Number(activeGroupRatePlanIdRaw) : null;
      const sameAsExistingSelection =
        Number(selectedRoomTypeId || 0) === activeGroupTypeId &&
        selectedRatePlanNum === activeGroupRatePlanNum;
      const activeGroupAmountRaw = activeGroupItem?.price;
      const activeGroupAmount = parseFloat(activeGroupAmountRaw);
      const hasExistingSequenceAmount =
        activeGroupAmountRaw !== null &&
        activeGroupAmountRaw !== undefined &&
        activeGroupAmountRaw !== '' &&
        Number.isFinite(activeGroupAmount) &&
        activeGroupAmount >= 0;

      if (sameAsExistingSelection && hasExistingSequenceAmount) {
        if (!cancelled) {
          setAmountPerNight(String(Number(activeGroupAmount.toFixed(2))));
          setAmountError('');
        }
        return;
      }

      const fallbackRate = computeAmountPerNight(pricingMeta, currentAdults, currentChildren, existingAmount);
      const selectedRatePlan = selectableRatePlans.find(
        (rp) => Number(rp?.id || 0) === Number(selectedRatePlanId || 0)
      );
      const ratePlanCode = selectedRatePlan?.code || '';
      const effectiveRateDate = getItemDateStr(activeGroupItem) || moment().format('YYYY-MM-DD');

      if (!ratePlanCode) {
        if (!cancelled) {
          const nextAmount = fallbackRate > 0 ? fallbackRate : existingAmount;
          setAmountPerNight(nextAmount > 0 ? String(Number(nextAmount.toFixed(2))) : '');
          setAmountError('');
        }
        return;
      }

      try {
        const rateRes = await getRatesByRatePlan(
          Number(selectedRoomTypeId),
          effectiveRateDate,
          ratePlanCode
        );
        const rates = rateRes?.rates || {};
        const effectiveMeta = {
          ...(pricingMeta || {}),
          baseRate: parseRate(rates?.base_rate ?? rates?.final_rate, pricingMeta?.baseRate ?? fallbackRate),
          singleRate: parseRate(rates?.single_occupancy_rate, rates?.base_rate ?? pricingMeta?.singleRate ?? fallbackRate),
          extraAdultRate: parseRate(rates?.extra_adult_rate, pricingMeta?.extraAdultRate ?? 0),
          extraChildRate: parseRate(rates?.extra_child_rate, pricingMeta?.extraChildRate ?? 0),
        };
        const nextAmount = computeAmountPerNight(effectiveMeta, currentAdults, currentChildren, fallbackRate);
        if (!cancelled) {
          const safeAmount = nextAmount > 0 ? nextAmount : existingAmount;
          setAmountPerNight(safeAmount > 0 ? String(Number(safeAmount.toFixed(2))) : '');
          setAmountError('');
        }
      } catch (_) {
        if (!cancelled) {
          const nextAmount = fallbackRate > 0 ? fallbackRate : existingAmount;
          setAmountPerNight(nextAmount > 0 ? String(Number(nextAmount.toFixed(2))) : '');
          setAmountError('');
        }
      }
    };

    syncAmountFromDailyRate();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    selectedRoomTypeId,
    selectedRatePlanId,
    selectableRatePlans,
    activeGroupItem,
    getItemDateStr,
    getRoomTypePricingMeta,
    computeAmountPerNight,
    parseRate,
  ]);

  const currentRtId = activeGroupItem && (activeGroupItem.room_type_id ?? activeGroupItem.room_type?.id);

  const allocationRoomTypes = allocationData?.room_types || [];
  const selectedRoomTypeGroup = allocationRoomTypes.find(
    (rt) => (rt.room_type?.id ?? rt.id) === selectedRoomTypeId
  );
  const roomsForType = selectedRoomTypeGroup?.rooms || [];
  const ownStreamRoomIds = useMemo(() => {
    const ids = new Set();
    const todayStr = moment().format('YYYY-MM-DD');
    (group?.items || []).forEach((item) => {
      const roomId = Number((item?.allocated_room_id ?? item?.allocated_room?.id) || 0);
      if (!roomId) return;
      const itemDateStr = item?.date
        ? (moment.isMoment(item.date) ? item.date.format('YYYY-MM-DD') : String(item.date).slice(0, 10))
        : '';
      if (!itemDateStr || itemDateStr >= todayStr) {
        ids.add(roomId);
      }
    });
    return ids;
  }, [group?.items]);

  // In upgrade flow, allow only truly unallocated rooms in UI:
  // - available against other bookings
  // - not already allocated anywhere in this same booking (other sequence/type)
  // - keep current stream room(s) visible for proper default selection/editing
  const allocatedRoomIdsInSameBooking = useMemo(() => {
    const ids = new Set();
    (allocationData?.booking_items || []).forEach((item) => {
      const roomId = Number(item?.allocated_room_id || 0);
      if (roomId) ids.add(roomId);
    });
    return ids;
  }, [allocationData?.booking_items]);
  const availableRoomsForType = roomsForType.filter((room) => {
    const roomId = Number(room?.id || 0);
    if (!roomId) return false;
    const isOwnStreamRoom = ownStreamRoomIds.has(roomId);
    if (isOwnStreamRoom) return true;
    // Keep occupied / pending-checkout rooms visible so staff can see why they are blocked.
    return !allocatedRoomIdsInSameBooking.has(roomId);
  });

  const isUpgradeRoomHeld = useCallback((room) => {
    if (!room) return false;
    if (ownStreamRoomIds.has(Number(room.id))) return false;
    return Boolean(getCheckInRoomHoldReason(room));
  }, [ownStreamRoomIds]);

  const selectableRoomsByTypeId = useMemo(() => {
    const counts = {};
    (allocationData?.room_types || []).forEach((group) => {
      const typeId = Number(group?.room_type?.id ?? group?.id ?? 0);
      if (!typeId) return;
      counts[typeId] = (group.rooms || []).filter((room) => {
        const roomId = Number(room?.id || 0);
        if (!roomId) return false;
        if (ownStreamRoomIds.has(roomId)) return false;
        if (allocatedRoomIdsInSameBooking.has(roomId)) return false;
        return !getCheckInRoomHoldReason(room);
      }).length;
    });
    return counts;
  }, [allocationData?.room_types, ownStreamRoomIds, allocatedRoomIdsInSameBooking]);

  const roomTypeSelectOptions = useMemo(
    () => availableRoomTypes.map((rt) => {
      const typeId = Number(rt.id);
      const physicalCount = selectableRoomsByTypeId[typeId];
      return {
        id: rt.id,
        name: rt.name || rt.room_type?.name,
        available_rooms: physicalCount != null ? physicalCount : (rt.available_rooms ?? 0),
        is_sold_out: rt.is_sold_out,
        is_blocked: rt.is_blocked,
      };
    }),
    [availableRoomTypes, selectableRoomsByTypeId],
  );

  useEffect(() => {
    if (!open) return;
    if (!roomTypeSelectOptions.length) {
      setSelectedRoomTypeId(null);
      return;
    }
    const hasSelectedType = roomTypeSelectOptions.some(
      (rt) => Number(rt?.id || 0) === Number(selectedRoomTypeId || 0)
    );
    if (!hasSelectedType) {
      setSelectedRoomTypeId(roomTypeSelectOptions[0].id);
      setSelectedRoomId(null);
    }
  }, [open, roomTypeSelectOptions, selectedRoomTypeId]);

  const selectedUpgradeRoom = useMemo(
    () => availableRoomsForType.find((room) => Number(room.id) === Number(selectedRoomId)) || null,
    [availableRoomsForType, selectedRoomId],
  );
  const selectedUpgradeHoldReason = isUpgradeRoomHeld(selectedUpgradeRoom)
    ? getCheckInRoomHoldReason(selectedUpgradeRoom)
    : '';
  const selectedUpgradeHoldTooltip = selectedUpgradeHoldReason
    ? getCheckInRoomHoldTooltip(
        selectedUpgradeHoldReason,
        selectedUpgradeRoom?.room_number,
        'upgrade',
      )
    : '';

  // If selected room becomes invalid after filtering (e.g. currently allocated in same booking),
  // clear it to prevent accidental submit with hidden room selection.
  useEffect(() => {
    if (!selectedRoomId) return;
    const stillVisible = availableRoomsForType.some((room) => Number(room.id) === Number(selectedRoomId));
    if (!stillVisible || isUpgradeRoomHeld(selectedUpgradeRoom)) {
      setSelectedRoomId(null);
    }
  }, [selectedRoomId, availableRoomsForType, selectedUpgradeRoom, isUpgradeRoomHeld]);

  const amountNum = parseFloat(amountPerNight);
  const isAmountValid = amountPerNight !== '' && !Number.isNaN(amountNum) && amountNum >= 0;
  const selectedTypeMeta = getRoomTypePricingMeta(selectedRoomTypeId);
  const maxGuests = Math.max(1, Number(selectedTypeMeta?.maxGuests || 4) || 4);
  const maxAdults = Math.max(0, maxGuests - Number(upgradeChildren || 0));
  const maxChildren = Math.max(0, maxGuests - Number(upgradeAdults || 0));
  const canSubmit = selectedRoomTypeId != null && isAmountValid;

  useEffect(() => {
    const safeChildren = Math.max(0, Math.min(Number(upgradeChildren || 0), maxGuests));
    const safeAdults = Math.max(0, Math.min(Number(upgradeAdults || 0), Math.max(0, maxGuests - safeChildren)));
    if (safeChildren !== Number(upgradeChildren || 0)) {
      setUpgradeChildren(safeChildren);
    }
    if (safeAdults !== Number(upgradeAdults || 0)) {
      setUpgradeAdults(safeAdults);
    }
  }, [maxGuests, upgradeAdults, upgradeChildren]);

  const handleUpgrade = () => {
    if (!canSubmit || !group?.items?.length || !onUpgradeSuccess) return;
    setAmountError('');
    setError(null);
    setSubmitting(true);
    try {
      const todayStr = moment().format('YYYY-MM-DD');
      const selectedTypeNum = Number(selectedRoomTypeId || 0);
      const currentTypeNum = Number(currentRtId || 0);
      const isRoomTypeChange = !!selectedTypeNum && !!currentTypeNum && selectedTypeNum !== currentTypeNum;
      const reusingCurrentRoom = !!selectedRoomId && (group.items || []).some((it) => {
        const itemRoomId = Number((it?.allocated_room_id ?? it?.allocated_room?.id) || 0);
        if (!itemRoomId || itemRoomId !== Number(selectedRoomId)) return false;
        const itemDateStr = it?.date
          ? (moment.isMoment(it.date) ? it.date.format('YYYY-MM-DD') : String(it.date).slice(0, 10))
          : '';
        // Validate current/future stream (upgrade applies from today).
        return !itemDateStr || itemDateStr >= todayStr;
      });
      if (isRoomTypeChange && reusingCurrentRoom) {
        setError('Please select a different room for upgrade. Current room cannot be reused.');
        setSubmitting(false);
        return;
      }
      if (selectedUpgradeHoldReason) {
        setError(selectedUpgradeHoldTooltip || 'Selected room is not available for upgrade.');
        setSubmitting(false);
        return;
      }
      const itemIds = group.items.map((it) => it.id).filter(Boolean);
      const fromDate = moment().format('YYYY-MM-DD');
      const price = Number.isNaN(amountNum) || amountNum < 0 ? 0 : Math.round(amountNum * 100) / 100;
      onUpgradeSuccess({
        itemIds,
        roomTypeId: selectedRoomTypeId,
        ratePlanId: selectedRatePlanId ?? null,
        allocatedRoomId: selectedRoomId || null,
        adults: Math.max(0, Number(upgradeAdults || 0)),
        children: Math.max(0, Number(upgradeChildren || 0)),
        amountPerNight: price,
        upgradeFromDate: fromDate,
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to apply upgrade.');
    } finally {
      setSubmitting(false);
    }
  };

  const textPrimary = isDark ? drawerTheme.text : '#333';
  const textSecondary = isDark ? drawerTheme.subtleText : '#666';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header - same as RoomAllocationDrawer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 24 }} />
            <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 600 }}>
              Upgrade Room
            </Typography>
            {booking?.code && (
              <>
                <Typography variant="body2" sx={{ color: textSecondary, mx: 0.5 }}>-</Typography>
                <Chip
                  label={booking.code}
                  size="small"
                  sx={{
                    backgroundColor: CUSTOM_BORDER_COLOR,
                    color: '#000',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                  }}
                />
              </>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: textSecondary, p: 0.5 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && (
            <>
              {error && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography>
              )}

              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 2,
                  backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                  border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                  borderRadius: '12px',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { minHeight: 48 } }}>
                      <InputLabel
                        sx={{
                          color: isDark ? drawerTheme.subtleText : '#666',
                          '&.Mui-focused': { color: CUSTOM_BORDER_COLOR },
                        }}
                      >
                        Room Type
                      </InputLabel>
                      <Select
                        value={selectedRoomTypeId ?? ''}
                        MenuProps={selectMenuProps}
                        onChange={(e) => {
                          setSelectedRoomTypeId(e.target.value ? Number(e.target.value) : null);
                          setSelectedRoomId(null);
                        }}
                        label="Room Type"
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                          color: textPrimary,
                          '& .MuiSelect-select': { color: textPrimary },
                          '& .MuiInputLabel-root': { color: isDark ? drawerTheme.subtleText : '#666' },
                          '& .MuiInputLabel-root.Mui-focused': { color: CUSTOM_BORDER_COLOR },
                        }}
                      >
                        {roomTypeSelectOptions.map((rt) => {
                          const avail = rt.available_rooms ?? 0;
                          const label =
                            rt.name +
                            (Number(rt.id) === Number(currentRtId || 0) ? ' (current)' : '') +
                            (avail !== undefined ? ` (${avail} available)` : '');
                          const disabled =
                            rt.is_blocked ||
                            (rt.is_sold_out && rt.id !== (activeGroupItem?.room_type_id ?? activeGroupItem?.room_type?.id));
                          return (
                            <MenuItem key={rt.id} value={rt.id} disabled={disabled}>
                              {label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { minHeight: 48 } }}>
                      <InputLabel
                        sx={{
                          color: isDark ? drawerTheme.subtleText : '#666',
                          '&.Mui-focused': { color: CUSTOM_BORDER_COLOR },
                        }}
                      >
                        {isOtaBooking ? 'Rate Plan' : 'Meal Plan'}
                      </InputLabel>
                      <Select
                        value={selectedRatePlanId ?? ''}
                        MenuProps={selectMenuProps}
                        onChange={(e) => setSelectedRatePlanId(e.target.value ? Number(e.target.value) : null)}
                        label={isOtaBooking ? 'Rate Plan' : 'Meal Plan'}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                          color: textPrimary,
                          '& .MuiSelect-select': { color: textPrimary },
                          '& .MuiInputLabel-root': { color: isDark ? drawerTheme.subtleText : '#666' },
                          '& .MuiInputLabel-root.Mui-focused': { color: CUSTOM_BORDER_COLOR },
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        {selectableRatePlans.map((rp) => (
                          <MenuItem key={rp.id} value={rp.id}>
                            {rp.name || rp.code}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: textPrimary }}>
                        Adults (Max: {maxAdults})
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          color="primary"
                          onClick={() => setUpgradeAdults((prev) => Math.max(0, Number(prev || 0) - 1))}
                          disabled={upgradeAdults <= 0}
                          sx={{
                            border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                            '&:hover': { border: `1px solid ${CUSTOM_BORDER_COLOR}`, backgroundColor: `${CUSTOM_BORDER_COLOR}20` },
                            '&.Mui-disabled': { border: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}` },
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ minWidth: 44, textAlign: 'center', color: textPrimary, fontWeight: 500 }}>
                          {upgradeAdults || 0}
                        </Typography>
                        <IconButton
                          color="primary"
                          onClick={() => setUpgradeAdults((prev) => Math.min(maxAdults, Number(prev || 0) + 1))}
                          disabled={upgradeAdults >= maxAdults}
                          sx={{
                            border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                            '&:hover': { border: `1px solid ${CUSTOM_BORDER_COLOR}`, backgroundColor: `${CUSTOM_BORDER_COLOR}20` },
                            '&.Mui-disabled': { border: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}` },
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: textPrimary }}>
                        Children (Max: {maxChildren})
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          color="primary"
                          onClick={() => setUpgradeChildren((prev) => Math.max(0, Number(prev || 0) - 1))}
                          disabled={upgradeChildren <= 0}
                          sx={{
                            border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                            '&:hover': { border: `1px solid ${CUSTOM_BORDER_COLOR}`, backgroundColor: `${CUSTOM_BORDER_COLOR}20` },
                            '&.Mui-disabled': { border: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}` },
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ minWidth: 44, textAlign: 'center', color: textPrimary, fontWeight: 500 }}>
                          {upgradeChildren || 0}
                        </Typography>
                        <IconButton
                          color="primary"
                          onClick={() => setUpgradeChildren((prev) => Math.min(maxChildren, Number(prev || 0) + 1))}
                          disabled={upgradeChildren >= maxChildren}
                          sx={{
                            border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                            '&:hover': { border: `1px solid ${CUSTOM_BORDER_COLOR}`, backgroundColor: `${CUSTOM_BORDER_COLOR}20` },
                            '&.Mui-disabled': { border: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}` },
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Amount per day"
                      type="number"
                      value={amountPerNight}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAmountPerNight(v);
                        setAmountError('');
                      }}
                      onBlur={() => {
                        const n = parseFloat(amountPerNight);
                        if (amountPerNight !== '' && (Number.isNaN(n) || n < 0)) setAmountError('Enter a valid amount ≥ 0');
                        else setAmountError('');
                      }}
                      error={!!amountError}
                      helperText={amountError}
                      inputProps={{ min: 0, step: 0.01 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {bookingCurrencySymbol}
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': { minHeight: 48 },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                        color: textPrimary,
                        '& .MuiInputLabel-root.Mui-focused': { color: CUSTOM_BORDER_COLOR },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mt: -0.5 }}>
                      Upgrade applies from current date.
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Typography variant="subtitle1" sx={{ color: textPrimary, fontWeight: 600, mb: 1.5 }}>
                Select Room
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: textSecondary }}>
                  Choose one room for this stay. Optional — leave unassigned if needed.
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {availableRoomsForType.map((room) => {
                  const isRoomSelected = selectedRoomId === room.id;
                  const roomType = selectedRoomTypeGroup?.room_type;
                  const roomImage = roomType?.image || roomType?.image_url;
                  const holdReason = isUpgradeRoomHeld(room) ? getCheckInRoomHoldReason(room) : '';
                  const holdLabel = getCheckInRoomHoldLabel(holdReason);
                  const holdTooltip = getCheckInRoomHoldTooltip(holdReason, room.room_number, 'upgrade');
                  const toggleRoom = () => {
                    if (holdReason) return;
                    setSelectedRoomId(isRoomSelected ? null : room.id);
                  };
                  return (
                    <Grid item xs={6} sm={4} md={3} key={room.id}>
                      <Card
                        elevation={1}
                        onClick={toggleRoom}
                        sx={{
                          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          overflow: 'hidden',
                          cursor: holdReason ? 'not-allowed' : 'pointer',
                          opacity: holdReason ? 0.72 : 1,
                          '&:hover': holdReason ? {} : {
                            transform: 'translateY(-2px)',
                            boxShadow: isDark
                              ? '0 8px 16px rgba(76, 225, 182, 0.3)'
                              : '0 8px 16px rgba(76, 225, 182, 0.2)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                          <Box sx={{ position: 'relative', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box
                              sx={{
                                flex: 1,
                                minHeight: 120,
                                position: 'relative',
                                backgroundImage: roomImage
                                  ? `url(${roomImage})`
                                  : `linear-gradient(135deg, ${isDark ? '#3a3a4a' : '#e8e8e8'} 0%, ${isDark ? '#2a2a3a' : '#d8d8d8'} 100%)`,
                                backgroundColor: isDark ? '#3a3a4a' : '#e8e8e8',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {!roomImage && (
                                <RoomIcon sx={{ fontSize: 32, color: isDark ? drawerTheme.subtleText : '#999', opacity: 0.5 }} />
                              )}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '50%',
                                  background: `linear-gradient(180deg, 
                                    ${isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.6)'} 0%, 
                                    ${isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.4)'} 50%, 
                                    transparent 100%)`,
                                  pointerEvents: 'none',
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  left: 8,
                                  right: 8,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  zIndex: 2,
                                }}
                              >
                                <Checkbox
                                  checked={isRoomSelected}
                                  disabled={Boolean(holdReason)}
                                  size="small"
                                  sx={{
                                    color: '#fff',
                                    backgroundColor: isRoomSelected
                                      ? 'rgba(76, 225, 182, 0.9)'
                                      : 'rgba(0, 0, 0, 0.4)',
                                    borderRadius: '4px',
                                    padding: '2px',
                                    backdropFilter: 'blur(8px)',
                                    '&.Mui-checked': {
                                      color: '#000',
                                      backgroundColor: 'rgba(76, 225, 182, 0.9)',
                                    },
                                    '&:hover': {
                                      backgroundColor: isRoomSelected
                                        ? 'rgba(76, 225, 182, 1)'
                                        : 'rgba(0, 0, 0, 0.6)',
                                    },
                                    '& .MuiSvgIcon-root': {
                                      fontSize: 20,
                                      filter: isRoomSelected ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRoom();
                                  }}
                                />
                                <Chip
                                  icon={<RoomIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                                  label={room.room_number ?? room.id}
                                  size="small"
                                  sx={{
                                    backgroundColor: isRoomSelected
                                      ? 'rgba(76, 225, 182, 0.9)'
                                      : 'rgba(0, 0, 0, 0.5)',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    height: 28,
                                    backdropFilter: 'blur(10px)',
                                    border: `1.5px solid ${isRoomSelected ? 'rgba(76, 225, 182, 1)' : 'rgba(255, 255, 255, 0.3)'}`,
                                    boxShadow: isRoomSelected
                                      ? '0 4px 12px rgba(76, 225, 182, 0.4), 0 2px 4px rgba(0,0,0,0.3)'
                                      : '0 2px 8px rgba(0,0,0,0.4)',
                                    letterSpacing: '0.5px',
                                    transition: 'all 0.3s ease',
                                    '& .MuiChip-icon': {
                                      color: '#fff !important',
                                      marginLeft: '8px',
                                    },
                                    '&:hover': {
                                      backgroundColor: isRoomSelected
                                        ? 'rgba(76, 225, 182, 1)'
                                        : 'rgba(0, 0, 0, 0.7)',
                                      transform: 'translateY(-1px)',
                                      boxShadow: isRoomSelected
                                        ? '0 6px 16px rgba(76, 225, 182, 0.5), 0 2px 4px rgba(0,0,0,0.3)'
                                        : '0 4px 12px rgba(0,0,0,0.5)',
                                    },
                                  }}
                                />
                              </Box>
                              {holdReason ? (
                                <Tooltip title={holdTooltip} arrow>
                                  <Chip
                                    label={holdLabel}
                                    size="small"
                                    sx={getCheckInRoomHoldOverlaySx(
                                      getCheckInRoomHoldChipColor(holdReason),
                                      isDark,
                                    )}
                                  />
                                </Tooltip>
                              ) : null}
                            </Box>
                            <Box
                              sx={{
                                p: 1.25,
                                backgroundColor: isDark ? 'rgba(42, 42, 49, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)',
                                borderTop: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isDark ? drawerTheme.text : '#333',
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  display: 'block',
                                  textAlign: 'center',
                                }}
                              >
                                {roomType?.name ?? `Room ${room.room_number ?? room.id}`}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              {availableRoomsForType.length === 0 && selectedRoomTypeId && (
                <Typography variant="body2" sx={{ color: textSecondary, py: 2 }}>
                  No rooms listed for this type. You can upgrade without assigning a room.
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Footer - same as RoomAllocationDrawer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            display: 'flex',
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            disabled={submitting}
            sx={{
              borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
              color: isDark ? drawerTheme.text : '#666',
              '&:hover': {
                borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
              },
            }}
          >
            Cancel
          </Button>
          <Tooltip title={selectedUpgradeHoldTooltip} arrow>
            <span style={{ width: '100%' }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleUpgrade}
                disabled={submitting || !canSubmit || Boolean(selectedUpgradeHoldReason)}
                sx={{
                  backgroundColor: CUSTOM_BORDER_COLOR,
                  color: '#000',
                  '&:hover': { backgroundColor: '#3ab896' },
                  '&:disabled': {
                    backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                    color: isDark ? drawerTheme.subtleText : '#666',
                  },
                }}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Upgrade'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
};

export default UpgradeRoomDrawer;
